import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../../utils/AuthContext';
import { designSystem, commonStyles } from '../../styles/design-system';
import { EyeIcon, LockIcon, UserIcon } from '../../utils/IconWrapper';

const LoginWrapper = styled.div`
  ${commonStyles.flexCenter}
  min-height: 100vh;
  padding: ${designSystem.spacing.xl};
  background: ${designSystem.colors.background.primary};
  font-family: ${designSystem.typography.fontFamily.primary};

  @media (max-width: ${designSystem.breakpoints.tablet}) {
    padding: ${designSystem.spacing.md};
  }
`;

const LoginCard = styled(motion.div)`
  ${commonStyles.card}
  width: 100%;
  max-width: 450px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: ${designSystem.colors.background.gradient};
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${designSystem.spacing['2xl']};

  .logo {
    font-size: ${designSystem.typography.fontSize['4xl']};
    margin-bottom: ${designSystem.spacing.md};
  }

  h1 {
    ${commonStyles.gradientText}
    font-family: ${designSystem.typography.fontFamily.secondary};
    font-size: ${designSystem.typography.fontSize['3xl']};
    font-weight: ${designSystem.typography.fontWeight.bold};
    margin: 0 0 ${designSystem.spacing.sm} 0;
  }

  p {
    color: ${designSystem.colors.text.secondary};
    font-size: ${designSystem.typography.fontSize.base};
    margin: 0;
  }
`;

const Form = styled.form`
  width: 100%;
`;

const FormGroup = styled.div`
  margin-bottom: ${designSystem.spacing.lg};
  position: relative;
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${designSystem.spacing.sm};
  color: ${designSystem.colors.text.primary};
  font-weight: ${designSystem.typography.fontWeight.semibold};
  font-size: ${designSystem.typography.fontSize.sm};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  .icon {
    position: absolute;
    left: ${designSystem.spacing.md};
    color: ${designSystem.colors.text.secondary};
    z-index: 1;
    transition: color ${designSystem.transitions.normal};
  }

  &:focus-within .icon {
    color: ${designSystem.colors.primary.main};
  }
`;

const Input = styled.input`
  ${commonStyles.input}
  padding-left: ${designSystem.spacing['3xl']};
  
  &::placeholder {
    color: ${designSystem.colors.text.secondary};
    font-style: italic;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${designSystem.spacing.md};
  background: none;
  border: none;
  color: ${designSystem.colors.text.secondary};
  cursor: pointer;
  padding: ${designSystem.spacing.xs};
  border-radius: ${designSystem.borderRadius.sm};
  transition: all ${designSystem.transitions.normal};

  &:hover {
    color: ${designSystem.colors.primary.main};
    background: ${designSystem.colors.background.tertiary};
  }
`;

const LoginButton = styled.button`
  ${commonStyles.buttonPrimary}
  width: 100%;
  margin-bottom: ${designSystem.spacing.lg};
  font-size: ${designSystem.typography.fontSize.base};
  font-weight: ${designSystem.typography.fontWeight.bold};
  padding: ${designSystem.spacing.lg};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;

    &::before {
      display: none;
    }
  }
`;

const ErrorMessage = styled(motion.div)`
  background: ${designSystem.colors.status.error}15;
  border: 1px solid ${designSystem.colors.status.error}30;
  border-radius: ${designSystem.borderRadius.lg};
  padding: ${designSystem.spacing.md};
  margin-top: ${designSystem.spacing.lg};
  color: ${designSystem.colors.status.error};
  font-size: ${designSystem.typography.fontSize.sm};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${designSystem.spacing.sm};

  &::before {
    content: '⚠️';
  }
`;

const LoadingSpinner = styled.div`
  ${commonStyles.loading}
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  margin-right: ${designSystem.spacing.sm};
`;

const Footer = styled.div`
  text-align: center;
  padding-top: ${designSystem.spacing.lg};
  border-top: 1px solid ${designSystem.colors.border.light};
  color: ${designSystem.colors.text.secondary};
  font-size: ${designSystem.typography.fontSize.sm};

  .version {
    font-family: ${designSystem.typography.fontFamily.primary};
    opacity: 0.7;
  }
`;

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login, error } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await login(email, password);
    
    setIsLoading(false);
    
    if (result.success) {
      navigate('/admin/dashboard');
    }
  };

  return (
    <LoginWrapper>
      <LoginCard
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <Header>
          <div className="logo">🏛️</div>
          <h1>Pannello Amministrativo</h1>
          <p>Journey of History - Sistema di Gestione</p>
        </Header>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="email">Email Amministratore</Label>
            <InputWrapper>
              <UserIcon className="icon" size={18} />
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@j-o-h.com"
                required
                autoComplete="email"
              />
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <InputWrapper>
              <LockIcon className="icon" size={18} />
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
              <PasswordToggle
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
              >
                <EyeIcon size={18} />
              </PasswordToggle>
            </InputWrapper>
          </FormGroup>

          <LoginButton 
            type="submit" 
            disabled={isLoading || !email || !password}
          >
            {isLoading && <LoadingSpinner />}
            {isLoading ? 'Accesso in corso...' : '🔐 Accedi al Pannello'}
          </LoginButton>

          {error && (
            <ErrorMessage
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </ErrorMessage>
          )}
        </Form>

        <Footer>
          <div className="version">J-O-H Admin Panel v1.0</div>
        </Footer>
      </LoginCard>
    </LoginWrapper>
  );
};

export default Login; 